import { PeopleRequestError, type AccessChange, type Person, type Role } from '@/lib/bff/people'
import type { Translator } from '@/lib/i18n'

export const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const BUILT_IN_ROLE_IDS = new Set(['ORGANISATION_ADMIN', 'SYSTEM_ADMIN'])

/** A built-in role's name in the UI language; a custom role's tenant-given name, which is never translated. */
export function roleLabel(t: Translator, role: Pick<Role, 'id' | 'kind' | 'name'>): string {
  if (role.kind === 'builtIn' || role.name === null) return builtInLabel(t, role.id)
  return role.name
}

/** An access-change role label: a stored built-in name is translated, anything else is shown as recorded. */
export function builtInLabel(t: Translator, label: string): string {
  return BUILT_IN_ROLE_IDS.has(label) ? t(`people.builtInRoles.${label}`) : label
}

export function builtInDescription(t: Translator, roleId: string, tenant: string): string | undefined {
  if (roleId === 'ORGANISATION_ADMIN') return t('people.rolesPage.orgAdminDescription', { tenant })
  if (roleId === 'SYSTEM_ADMIN') return t('people.rolesPage.sysAdminDescription')
  return undefined
}

export const personName = (person: Pick<Person, 'name' | 'email' | 'userId'>) => person.name ?? person.email ?? person.userId

export const errorCode = (error: unknown) => (error instanceof PeopleRequestError ? error.code : null)
export const errorStatus = (error: unknown) => (error instanceof PeopleRequestError ? error.status : null)

/* TECH-DEBT(VIS-327): lists of role names in en-GB, as the dates are, until the tenant formatter exists. */
const listFormat = new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' })
export const formatList = (items: string[]) => listFormat.format(items)

/** Moves focus to an element inside `root` after the next paint; a non-control target gets tabindex -1. */
export function focusWithin(root: HTMLElement | null, selector: string) {
  requestAnimationFrame(() => {
    const target = root?.querySelector<HTMLElement>(selector)
    if (!target) return
    if (!target.matches('button, a, input')) target.setAttribute('tabindex', '-1')
    target.focus()
  })
}

/** The number of entries that differ between two sets of ids. */
export function changedCount(initial: string[], selected: string[]) {
  return initial.filter((id) => !selected.includes(id)).length + selected.filter((id) => !initial.includes(id)).length
}

/** An access change in words, from its kind and its added and removed role labels (or, for a role edit, action ids). */
export function describeChange(t: Translator, change: AccessChange): string {
  const added = change.change.added.map((label) => builtInLabel(t, label))
  const removed = change.change.removed.map((label) => builtInLabel(t, label))
  switch (change.kind) {
    case 'invited':
      return added.length > 0
        ? t('people.changes.kind.invited', { roles: formatList(added) })
        : t('people.changes.kind.invitedNoRoles')
    case 'invitationWithdrawn':
      return t('people.changes.kind.invitationWithdrawn')
    case 'invitationSentAgain':
      return added.length > 0
        ? t('people.changes.kind.invitationSentAgain', { roles: formatList(added) })
        : t('people.changes.kind.invitationSentAgainNoRoles')
    case 'rolesChanged':
      if (added.length > 0 && removed.length > 0) {
        return t('people.changes.kind.rolesAddedRemoved', { added: formatList(added), removed: formatList(removed) })
      }
      return removed.length > 0
        ? t('people.changes.kind.rolesRemoved', { removed: formatList(removed) })
        : t('people.changes.kind.rolesAdded', { added: formatList(added) })
    case 'accessRemoved':
      return t('people.changes.kind.accessRemoved')
    case 'roleCreated':
      return t('people.changes.kind.roleCreated', { count: change.change.added.length })
    case 'roleEdited':
      return t('people.changes.kind.roleEdited', {
        added: change.change.added.length,
        removed: change.change.removed.length,
      })
    case 'roleDeleted':
      return t('people.changes.kind.roleDeleted')
  }
}
